'use client'

import { useEffect, useState } from 'react'
import { deserializeFloorplan } from '@ideight/engine/model'
import { api } from '../../lib/api'

/**
 * Lightweight top-down thumbnail of a sample home, drawn as an SVG of the
 * floorplan's wall segments (no WebGL — safe to render many per gallery
 * grid). Falls back to a house glyph if the floorplan can't be read.
 */
export function FloorplanThumb({ sampleId }: { readonly sampleId: string }): React.ReactElement {
  const [plan, setPlan] = useState<{ d: string; viewBox: string; stroke: number } | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    void api
      .getSampleFloorplan(sampleId)
      .then((data) => {
        if (cancelled) return
        if (!data) {
          setFailed(true)
          return
        }
        const fp = deserializeFloorplan(data)
        const corners = fp.getCorners()
        const walls = fp.getWalls()
        if (corners.length === 0 || walls.length === 0) {
          setFailed(true)
          return
        }
        const byId = new Map(corners.map((c) => [c.id, c]))
        const xs = corners.map((c) => c.x)
        const ys = corners.map((c) => c.y)
        const minX = Math.min(...xs)
        const minY = Math.min(...ys)
        const w = Math.max(1, Math.max(...xs) - minX)
        const h = Math.max(1, Math.max(...ys) - minY)
        const d = walls
          .map((wall) => {
            const a = byId.get(wall.startCornerId)
            const b = byId.get(wall.endCornerId)
            if (!a || !b) return ''
            return `M${a.x - minX} ${a.y - minY}L${b.x - minX} ${b.y - minY}`
          })
          .join('')
        if (d === '') {
          setFailed(true)
          return
        }
        setPlan({ d, viewBox: `0 0 ${w} ${h}`, stroke: Math.max(w, h) * 0.014 })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [sampleId])

  if (failed) return <span style={{ fontSize: 28, opacity: 0.5 }}>⌂</span>
  if (!plan) return <span style={{ fontSize: 12, color: '#6f6a61' }}>…</span>

  return (
    <svg
      viewBox={plan.viewBox}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      style={{ padding: 16, display: 'block' }}
      role="img"
      aria-label="Floor plan preview"
    >
      <path
        d={plan.d}
        fill="none"
        stroke="#c9a36a"
        strokeWidth={plan.stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.85}
      />
    </svg>
  )
}
